import { DeviceMode, isNodeV2, isShellNode, NodeV2Style } from '@vedalib/editor'
import { ButtonSchemaType } from '@vedalib/editor/lib/brand'
import { ButtonElementValue, EditorElement } from '@vedalib/editor/lib/elements'
import { generateId } from '@vedalib/editor/lib/utils/id'
import { DeepPartial } from '@vedalib/editor/lib/utils/types'
import * as R from 'ramda'

import { getParentNode } from 'components/Editor/EditorContext/selectors/block'
import { generateElement } from 'components/Editor/EditorElements/elements.config'
import { notify } from 'components/uiKit/Notification'
import { NotificationType } from 'components/uiKit/Notification/types'
import { setNavbar, setProjectNavigation, updateBlock } from 'services/Store/Project/actions'
import { NavigationEnum } from 'services/Store/Project/enums'
import { addNode, moveNode, rejectNode } from 'services/Store/Project/mutators/node'
import {
  getBlock,
  getDeviceMode,
  getSelectedNodeIds,
  mergeNodeStyle,
} from 'services/Store/Project/selectors'
import type { Block } from 'services/Store/Project/types'
import store from 'services/Store/store'
import { t } from 'services/Translation'

import { getGroupArea, getGroupCoords } from '../DisplayProvider/utils'
import { genNode } from '../constants'
import { safeParseArea, saveArea } from '../formControls/Area/utils'
import { safeParseCoords, saveCoords } from '../formControls/Coords/utils'

const STYLE_KEYS: (keyof NodeV2Style)[] = [
  'width',
  'height',
  'minHeight',
  'minWidth',
  'maxHeight',
  'maxWidth',
  'flexGrow',
  'flexShrink',
]

export const onWrapButton = async () => {
  const state = store.getState().project
  const dispatch = store.dispatch
  const block = getBlock(state)
  const device = getDeviceMode(state)
  const isDesktop = device === DeviceMode.desktop
  const selectedNodes = getSelectedNodeIds(state)
  const children = selectedNodes.map((id) => block?.schema.nodes[id]).filter(isNodeV2)
  const parent = getParentNode(selectedNodes?.[0], block)
  const parentStyle = mergeNodeStyle(parent?.style || {}, device)

  if (block && parent) {
    if (!isDesktop) {
      return notify({
        type: NotificationType.info,
        message: t('editorV2.notifyStructureChangesRestricted'),
      })
    }
    const ordered = R.sortBy((n) => {
      const style = mergeNodeStyle(n.style || {}, device)
      return parentStyle.display === 'grid'
        ? safeParseArea(style?.gridArea).rowStart
        : (safeParseCoords(style.coords).top ?? Infinity)
    }, children).reverse()
    const { rowChanges, colChanges } = ordered.reduce(
      (acc, node) => {
        const area = safeParseArea(node.style?.[device]?.gridArea)
        if (area.rowStart !== acc.prevRowStart) {
          acc.rowChanges += 1
        }
        if (area.colStart !== acc.prevColStart) {
          acc.colChanges += 1
        }
        acc.prevRowStart = area.rowStart
        acc.prevColStart = area.colStart
        return acc
      },
      { rowChanges: 0, colChanges: 0, prevRowStart: -1, prevColStart: -1 },
    )
    const dir = rowChanges >= colChanges ? 'column' : 'row'
    const index = Math.min(...ordered.map((node) => parent.children.indexOf(node.id)))
    if (ordered.length > 1 || isShellNode(ordered[0])) {
      const flex = genNode(generateId(), {
        style: {
          [DeviceMode.desktop]: {
            display: 'flex',
            flexDirection: dir,
            flexAlignItems: 'center',
            flexJustifyContent: 'center',
          },
        },
      })
      const cbs = ordered.map((node) => moveNode(node.id, flex.id, node.id, null)) as [
        (block: Block) => Block,
      ]
      const button = R.mergeDeepRight<
        EditorElement<ButtonElementValue, ButtonSchemaType>,
        DeepPartial<EditorElement<ButtonElementValue, ButtonSchemaType>>
      >(generateElement('button'), {
        style: {
          [DeviceMode.desktop]: {
            button: { backgroundColor: 'transparent', backgroundImage: null },
            disabled: { backgroundImage: null },
            border: { borderStyle: 'none', borderRadius: 0 },
            padding: { paddingTop: 0, paddingLeft: 0 },
          },
        },
        value: { item: { value: flex.id } },
      })
      const shell = genNode(generateId(), {
        elementId: button?.id,
        style: {
          [DeviceMode.desktop]: {
            gridArea: saveArea(getGroupArea(children, DeviceMode.desktop)),
            coords: saveCoords(getGroupCoords(parent, children, DeviceMode.desktop)),
            ...(ordered[0]?.elementId && R.pick(STYLE_KEYS, ordered[0].style?.desktop || {})),
          },
          [DeviceMode.tablet]: {
            ...(ordered[0]?.elementId &&
              R.pick([...STYLE_KEYS, 'gridArea', 'coords'], ordered[0].style?.tablet || {})),
          },
          [DeviceMode.mobile]: {
            ...(ordered[0]?.elementId &&
              R.pick([...STYLE_KEYS, 'gridArea', 'coords'], ordered[0].style?.mobile || {})),
          },
        },
      })

      dispatch(
        updateBlock({
          id: block.uuid,
          value: R.compose(
            ...cbs,
            addNode(
              parent.id,
              shell,
              { elements: { [button.id]: button }, nodes: { [flex.id]: flex } },
              { index },
            ),
          )(block),
        }),
      )
      dispatch(setNavbar({ tab: NavigationEnum.block }))
      dispatch(setProjectNavigation({ nodeIds: [shell.id] }))
    } else if (ordered.length === 1 && !isShellNode(ordered[0])) {
      const button = R.mergeDeepRight<
        EditorElement<ButtonElementValue, ButtonSchemaType>,
        DeepPartial<EditorElement<ButtonElementValue, ButtonSchemaType>>
      >(generateElement('button'), {
        style: {
          [DeviceMode.desktop]: {
            button: { backgroundColor: 'transparent', backgroundImage: null },
            disabled: { backgroundImage: null },
            border: { borderStyle: 'none', borderRadius: 0 },
            padding: { paddingTop: 0, paddingLeft: 0 },
          },
        },
        value: { item: { value: ordered[0].id } },
      })
      const shell = genNode(generateId(), {
        elementId: button?.id,
        style: {
          [DeviceMode.desktop]: R.pick(
            [...STYLE_KEYS, 'gridArea', 'coords'],
            ordered[0].style?.desktop || {},
          ),
          [DeviceMode.tablet]: R.pick(
            [...STYLE_KEYS, 'gridArea', 'coords'],
            ordered[0].style?.tablet || {},
          ),
          [DeviceMode.mobile]: R.pick(
            [...STYLE_KEYS, 'gridArea', 'coords'],
            ordered[0].style?.mobile || {},
          ),
        },
      })

      dispatch(
        updateBlock({
          id: block.uuid,
          value: R.compose(
            addNode(parent.id, shell, { elements: { [button.id]: button } }, { index }),
            rejectNode(ordered[0].id),
          )(block),
        }),
      )
      dispatch(setNavbar({ tab: NavigationEnum.block }))
      dispatch(setProjectNavigation({ nodeIds: [shell.id] }))
    }
  }
}
